import React, { useEffect, useState } from 'react';
import {
  BracketMatch,
  Player,
  PoolParticipant,
  SocketCallback,
} from '../room/types';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
import Countdown from 'react-countdown';
import { Socket } from 'socket.io-client';
import { useNavigate } from 'react-router-dom';
import { TourneyBracket } from './TourneyBracket';
import { PoolParticipants } from './PoolParticipants';
import { Stack } from '@mui/system';
import { Leaderboard } from './Leaderboard';
import { CreateBracketGameDialog } from '../../dialog/CreateBracketGameDialog';
import { UpdateBracketDataDialog } from '../../dialog/UpdateBracketDataDialog';
import { StyledPaperItem } from '../../common/mui-components';
import { Game } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';

type PropTypes = {
  handleSocketCallback: (res: SocketCallback) => void;
  profile: Profile;
  setGameId: (gameId: string | undefined) => void;
  socket: Socket;
};

export const PoolPlay = ({
  handleSocketCallback,
  profile,
  setGameId,
  socket,
}: PropTypes) => {
  const navigate = useNavigate();
  const [isRegistered, setIsRegistered] = useState(false);
  const [gameInProgress, setGameInProgress] = useState(false);
  const [poolParticipants, setPoolParticipants] = useState<PoolParticipant[]>();
  const [allPlayersReady, setAllPlayersReady] =
    useState<Record<string, Player>>();
  const [players, setPlayers] = useState<Record<string, Player>>();
  const [sortedPlayers, setSortedPlayers] = useState<Player[]>();
  const [bracketMatches, setBracketMatches] =
    useState<Record<string, BracketMatch>>();
  const [currentBracketMatchId, setCurrentBracketMatchId] = useState('00'); // 00 is the match key for 0 = quarterfinal, 0 = first match
  const [currentBracketMatch, setCurrentBracketMatch] =
    useState<BracketMatch>();
  const [openConfirmReadyModal, setOpenConfirmReadyModal] = useState<{
    open: boolean;
    username?: string;
  }>({ open: false });
  const [openCreateBracketGameDialog, setOpenCreateBracketGameDialog] =
    useState(false);
  const [openUpdateBracketDataDialog, setOpenUpdateBracketDataDialog] =
    useState(false);
  const [lobby, setLobby] = useState<Game[]>();

  useEffect(() => {
    setGameId('poolplay');
    socket.on('gameInProgress', setGameInProgress);
    socket.on('players', setPlayers);
    socket.on('poolParticipants', setPoolParticipants);
    socket.on('playerIsReady', setPlayers);
    socket.on('allPlayersReady', setAllPlayersReady);
    socket.on('bracketMatches', setBracketMatches);
    socket.on('currentBracketMatchId', setCurrentBracketMatchId);
    socket.on('requestBracket', setBracketMatches);
    socket.on('confirmReady', promptReadyCheck);
    socket.on('lobby', setLobby);
    socket.emit('requestPoolParticipants', {
      profileId: profile.id,
    });
    socket.emit(
      'requestPlayers',
      {
        gameId: 'poolplay',
        profileId: profile.id,
      },
      handleSocketCallback
    );
    socket.emit('requestBracket', {
      gameId: 'poolplay',
    });
    socket.emit('isGameInProgress', {
      gameId: 'poolplay',
    });
    return () => {
      socket.off('poolParticipants');
    };
  }, []);

  useEffect(() => {
    if (!players) return;
    setSortedPlayers(sortPlayers(players));
  }, [players]);

  useEffect(() => {
    if (!allPlayersReady) return;
    const playerMe = allPlayersReady[profile.id];
    if (playerMe) {
      navigate('/room');
    }
  }, [allPlayersReady]);

  useEffect(() => {
    const found = poolParticipants?.find((p) => p.profileId === profile.id);
    setIsRegistered(!!found);
  }, [poolParticipants]);

  useEffect(() => {
    if (!bracketMatches) return;
    setCurrentBracketMatch(bracketMatches[currentBracketMatchId]);
  }, [currentBracketMatchId, bracketMatches]);

  useEffect(() => {
    console.log('lobby', lobby);
  }, [lobby]);

  const sortPlayers = (players: Record<string, Player>) => {
    const sortedPlayers = Object.values(players)
      .sort((a, b) => a.parScore - b.parScore)
      .map((p, index) => ({
        ...p,
        rank: index + 1,
      }));
    return sortedPlayers;
  };

  const handleRegister = (isRegistered: boolean) => {
    if (isRegistered) {
      socket.emit('unregisterPoolParticipant', {
        profileId: profile.id,
      });
      return;
    }
    socket.emit(
      'registerPoolParticipant',
      {
        avatarUrl: profile.avatarUrl,
        handicap: profile.stats?.all || 'N/A',
        profileId: profile.id,
        username: profile.username,
        wins: profile.wins,
      },
      handleSocketCallback
    );
  };

  const handleAdminStart = () => {
    socket.emit('startPoolPlay');
  };

  const getCurrentPosition = () => {
    const self = sortedPlayers?.find((p) => p.profileId === profile.id);
    if (!gameInProgress) return 'TBD';
    if (!self || !sortedPlayers) return 'DNP';
    return `${self.rank} / ${sortedPlayers.length}`;
  };

  const handleRejoinGameClick = () => {
    socket.emit('canRejoinRoom', profile.id, (res: SocketCallback) => {
      if (res.status === 'ok') {
        navigate('/room', { state: { rejoin: true } });
      }
    });
  };

  const handleCreateBracketGameClick = (matchKey: string) => {
    socket.emit('createBracketGame', {
      matchKey: matchKey,
      profileId: profile.id,
    });
    setOpenCreateBracketGameDialog(false);
  };

  const handleUpdateBracketDataSubmit = (
    matchKey: string,
    match?: BracketMatch
  ) => {
    if (!match) return;
    socket.emit(
      'updateBracketMatch',
      { matchKey, match, profileId: profile.id },
      handleSocketCallback
    );
    setOpenUpdateBracketDataDialog(false);
  };

  const canJoinGame = (currentBracketMatch: BracketMatch) => {
    console.log('canJoinGame', currentBracketMatch);
    const { gameIds, profileIds } = currentBracketMatch;
    if (!gameIds.length) return false;
    if (profileIds.includes(profile.id)) return true;
    return false;
  };

  const handleJoinGameClick = (currentBracketMatch: BracketMatch) => {
    const { gameIds } = currentBracketMatch;
    setGameId(gameIds[gameIds.length - 1]);
    socket.emit(
      'joinBracketGame',
      { profileId: profile.id },
      handleSocketCallback
    );
  };

  const promptReadyCheck = (players: Record<string, Player>) => {
    console.log('promptReadyCheck', players);
    setPlayers(players);
    setOpenConfirmReadyModal({ open: true });
  };

  const handleReadyClick = () => {
    const ready = true;
    socket.emit('confirmedReady', profile.id, ready);
  };

  const handleReadyCancelClick = () => {
    const ready = false;
    socket.emit('confirmedReady', profile.id, ready);
    setOpenConfirmReadyModal({ open: false });
  };

  const didPlayerJoinBracketGame = (
    currentBracketMatch: BracketMatch,
    profileId: string
  ) => {
    const { gameIds } = currentBracketMatch;
    const currentGameId = gameIds[gameIds.length - 1];
    const foundGame = lobby?.find((g) => g.gameId === currentGameId);
    if (foundGame) {
      if (foundGame.activePlayers.includes(profileId)) return true;
    }
    return false;
  };

  return (
    <>
      {/* <ConfirmReadyModal
        handleReadyCancelClick={handleReadyCancelClick}
        handleReadyClick={handleReadyClick}
        mode={'pool'}
        openConfirmReadyModal={openConfirmReadyModal}
        players={players}
        profile={profile}
        ranked={false}
      ></ConfirmReadyModal> */}
      <CreateBracketGameDialog
        handleCreateBracketGameSubmit={handleCreateBracketGameClick}
        openCreateBracketGameDialog={openCreateBracketGameDialog}
        setOpenCreateBracketGameDialog={setOpenCreateBracketGameDialog}
      ></CreateBracketGameDialog>
      <UpdateBracketDataDialog
        bracketMatches={bracketMatches}
        handleUpdateBracketDataSubmit={handleUpdateBracketDataSubmit}
        openUpdateBracketDataDialog={openUpdateBracketDataDialog}
        setOpenUpdateBracketDataDialog={setOpenUpdateBracketDataDialog}
        sortedPlayers={sortedPlayers}
      ></UpdateBracketDataDialog>
      <Container maxWidth={'xl'} sx={{ flexGrow: 1, marginTop: 2 }}>
        {profile.admin ? (
          <Box
            sx={{
              marginBottom: 2,
              padding: 2,
              backgroundColor: 'primary.light',
              color: 'secondary.contrastText',
            }}
          >
            <Stack direction={'row'} spacing={3}>
              <Typography variant="h6">Admin Controls:</Typography>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setOpenCreateBracketGameDialog(true)}
              >
                CREATE GAME
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setOpenUpdateBracketDataDialog(true)}
              >
                UPDATE BRACKET
              </Button>
            </Stack>
          </Box>
        ) : null}
        <Stack direction={'row'} spacing={2}>
          <Box width={'70%'}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <StyledPaperItem>
                  <Typography variant="h5" mb={2}>
                    Starts in:
                  </Typography>
                  <Typography variant="h3" mb={3}>
                    <Countdown date={Date.now() + 10000000} />
                  </Typography>
                  {gameInProgress ? (
                    <Button
                      variant="contained"
                      size="large"
                      sx={{ marginBottom: 3 }}
                      onClick={handleRejoinGameClick}
                    >
                      {isRegistered ? 'Rejoin' : 'Specate'}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      size="large"
                      sx={{ marginBottom: 3 }}
                      onClick={() => handleRegister(isRegistered)}
                    >
                      {isRegistered ? 'Unregister' : 'Register'}
                    </Button>
                  )}
                </StyledPaperItem>
                <Button onClick={handleAdminStart}>Admin: Start</Button>
              </Grid>
              <Grid item xs={6}>
                <StyledPaperItem>
                  <Typography variant="h5" mb={2}>
                    Your results:
                  </Typography>
                  <Typography variant="h3" mb={3}>
                    {getCurrentPosition()}
                  </Typography>
                </StyledPaperItem>
              </Grid>
              <Grid item xs={8}>
                <StyledPaperItem>
                  <Typography variant="h5" mb={2}>
                    Bracket Matchups
                  </Typography>
                  <TourneyBracket
                    bracketMatches={bracketMatches}
                  ></TourneyBracket>
                </StyledPaperItem>
              </Grid>
              <Grid item xs={4}>
                <StyledPaperItem>
                  <Typography variant="h5" mb={2}>
                    Current Matchup
                  </Typography>
                  {currentBracketMatch ? (
                    <Stack
                      direction={'row'}
                      spacing={4}
                      justifyContent="center"
                    >
                      <Stack textAlign={'left'}>
                        <Typography variant="body1">
                          {currentBracketMatch?.usernames[0] || 'TBD'}
                        </Typography>
                        <Typography variant="body1">
                          {currentBracketMatch?.usernames[1] || 'TBD'}
                        </Typography>
                      </Stack>
                      <Stack textAlign={'left'}>
                        <Typography variant="body1">
                          {didPlayerJoinBracketGame(
                            currentBracketMatch,
                            currentBracketMatch.profileIds[0]
                          )
                            ? 'Joined'
                            : 'Has not joined'}
                        </Typography>
                        <Typography variant="body1">
                          {didPlayerJoinBracketGame(
                            currentBracketMatch,
                            currentBracketMatch.profileIds[1]
                          )
                            ? 'Joined'
                            : 'Has not joined'}
                        </Typography>
                      </Stack>
                    </Stack>
                  ) : null}
                  {currentBracketMatch && canJoinGame(currentBracketMatch) ? (
                    <Button
                      variant="contained"
                      size="large"
                      sx={{ marginTop: 2, marginBottom: 2 }}
                      onClick={() => handleJoinGameClick(currentBracketMatch)}
                      disabled={didPlayerJoinBracketGame(
                        currentBracketMatch,
                        profile.id
                      )}
                    >
                      Join
                    </Button>
                  ) : null}
                </StyledPaperItem>
              </Grid>
            </Grid>
          </Box>
          <Box width={'30%'}>
            <Grid container spacing={2}>
              <Grid item width={'100%'}>
                <StyledPaperItem>
                  {sortedPlayers?.length ? (
                    <Leaderboard players={sortedPlayers}></Leaderboard>
                  ) : (
                    <PoolParticipants
                      poolParticipants={poolParticipants}
                    ></PoolParticipants>
                  )}
                </StyledPaperItem>
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </Container>
    </>
  );
};
